import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Award,
  Clock,
  Sparkles,
  Building2,
  Heart,
  CheckCircle,
} from "lucide-react";
import VideoHero from "../components/VideoHero";
import Testimonials from "../components/Testimonials";
// Sections moved to About page
import { process, services } from "../data/constants";
import { usePage } from "../hooks/useApi";
import { pageApi } from "../services/api";

const HomePage: React.FC = () => {
  const { data: page, isLoading, error } = usePage("home");

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#FF5A1F] mx-auto mb-4"></div>
          <p className="text-[#334155]">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Error loading page: {error.message}
          </p>
          <p className="text-[#334155]">Using fallback content...</p>
        </div>
      </div>
    );
  }

  // Helper function to get field value with fallback
  const getField = (key: string, fallback: string = ""): string => {
    if (!page) return fallback;
    const fields = pageApi.getPageFields(page);
    return fields[key] || fallback;
  };

  // Helper function to get background images array
  const getBackgroundImages = (): string[] => {
    const imagesString = getField("hero_background_images");
    if (imagesString) {
      return imagesString.split(",").map((img) => img.trim());
    }
    return [
      "/images/hero1.jpeg",
      "/images/hero2.jpeg",
      "/images/hero3.jpeg",
      "/images/hero4.jpeg",
      "/images/DJI_0001-HDR.jpeg",
    ];
  };
  return (
    <div>
      {/* Video Hero Section */}
      <VideoHero
        title={getField(
          "hero_title",
          "Creating a Better Tomorrow, One Home at a Time"
        )}
        subtitle={getField(
          "hero_subtitle",
          "Welcome to Rolleston Homes, a trusted and reliable building company based in Rolleston, Christchurch. We are dedicated to providing high-quality construction services to our clients, whether you are looking to build a new home, renovate your existing property, or add an extension to your property."
        )}
        backgroundImages={getBackgroundImages()}
        backgroundVideo={getField(
          "hero_background_video",
          "/images/Social_media_reel_.mov"
        )}
        primaryCTA={{
          text: getField("hero_primary_button_text", "Get In Touch"),
          href: getField("hero_primary_button_link", "/contact"),
        }}
        secondaryCTA={{
          text: getField("hero_secondary_button_text", "View Our Projects"),
          href: getField("hero_secondary_button_link", "/homes"),
        }}
        enableTypewriter={true}
        enableStaggeredSubtitle={true}
      />

      {/* Animated Brand Statement Band */}
      <section className="bg-gradient-to-r from-[#FF5A1F] via-[#FF6B35] to-[#FF5A1F] py-20 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-64 h-64 animate-pulse">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white">
              <path d="M0,0 L200,50 L150,200 L0,150 Z" fill="currentColor" />
            </svg>
          </div>
          <div className="absolute bottom-0 right-0 w-64 h-64 animate-pulse delay-1000">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white">
              <path d="M50,0 L200,0 L200,200 L0,150 Z" fill="currentColor" />
            </svg>
          </div>
          {/* Floating particles */}
          <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white/30 rounded-full animate-bounce delay-500"></div>
          <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-white/20 rounded-full animate-bounce delay-700"></div>
          <div className="absolute top-1/2 left-3/4 w-2 h-2 bg-white/40 rounded-full animate-bounce delay-300"></div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <Sparkles className="w-8 h-8 text-white/80 mr-4 animate-spin" />
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">
              {getField("communities_title", "We're Building Communities")}
            </h2>
            <Sparkles className="w-8 h-8 text-white/80 ml-4 animate-spin delay-1000" />
          </div>
          <p className="text-xl text-white/95 max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up">
            {getField(
              "communities_subtitle",
              "With a commitment to excellence and a focus on innovation, we've established ourselves as a leader in the real estate and construction industry."
            )}
          </p>
          <Link
            to={getField("communities_button_link", "/about")}
            className="inline-flex items-center space-x-3 border-2 border-white text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-[#FF5A1F] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-105 group"
          >
            <span>
              {getField("communities_button_text", "Learn More About Us")}
            </span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      {/* Animated Stats Section */}
      <section className="py-20 bg-gradient-to-b from-[#FAFAFA] to-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-4">
              {getField("stats_title", "Building Dreams Since 2008")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "stats_subtitle",
                "Our commitment to excellence has made us Canterbury's most trusted home builder"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FF5A1F] to-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110">
                <Users className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-extrabold text-[#1F2937] mb-3 group-hover:text-[#FF5A1F] transition-colors duration-300">
                {getField("stat1_number", "500+")}
              </div>
              <div className="text-xl text-[#334155] font-semibold mb-2">
                {getField("stat1_label", "Happy Families")}
              </div>
              <div className="text-[#334155]">
                {getField(
                  "stat1_description",
                  "Homes delivered across Canterbury"
                )}
              </div>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-[#16A34A] to-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-extrabold text-[#1F2937] mb-3 group-hover:text-[#16A34A] transition-colors duration-300">
                {getField("stat2_number", "15+")}
              </div>
              <div className="text-xl text-[#334155] font-semibold mb-2">
                {getField("stat2_label", "Years Experience")}
              </div>
              <div className="text-[#334155]">
                {getField(
                  "stat2_description",
                  "Building quality homes in New Zealand"
                )}
              </div>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-extrabold text-[#1F2937] mb-3 group-hover:text-[#3B82F6] transition-colors duration-300">
                {getField("stat3_number", "98%")}
              </div>
              <div className="text-xl text-[#334155] font-semibold mb-2">
                {getField("stat3_label", "On-Time Delivery")}
              </div>
              <div className="text-[#334155]">
                {getField(
                  "stat3_description",
                  "Completed projects delivered on schedule"
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
            {getField("services_title", "Our Services")}
          </h1>
          <p className="text-lg text-[#334155] max-w-3xl mx-auto">
            {getField(
              "services_subtitle",
              "From initial concept to final completion, we provide comprehensive home building services that ensure your dream home becomes a reality. Our experienced team guides you through every step of the process."
            )}
          </p>
        </div>
      </section>

      {/* Services Grid - Orange Section */}
      <section className="py-16 bg-gradient-to-r from-[#FF5A1F] via-[#FF6B35] to-[#FF5A1F] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-64 h-64 animate-pulse">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white">
              <path d="M0,0 L200,50 L150,200 L0,150 Z" fill="currentColor" />
            </svg>
          </div>
          <div className="absolute bottom-0 right-0 w-64 h-64 animate-pulse delay-1000">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white">
              <path d="M50,0 L200,0 L200,200 L0,150 Z" fill="currentColor" />
            </svg>
          </div>
          {/* Floating particles */}
          <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white/30 rounded-full animate-bounce delay-500"></div>
          <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-white/20 rounded-full animate-bounce delay-700"></div>
          <div className="absolute top-1/2 left-3/4 w-2 h-2 bg-white/40 rounded-full animate-bounce delay-300"></div>
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-white/25 rounded-full animate-bounce delay-900"></div>
          <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-white/35 rounded-full animate-bounce delay-1100"></div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 hover:shadow-2xl hover:bg-white transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF5A1F] to-[#FF6B35] rounded-full flex items-center justify-center mb-6 shadow-lg">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1F2937] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#334155] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                      <span className="text-[#334155] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("process_title", "Our Building Process")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "process_subtitle",
                "We follow a proven process that ensures quality results and keeps you informed every step of the way."
              )}
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-8"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#FF5A1F] text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#334155] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Orange Background */}
      <section className="py-20 bg-gradient-to-r from-[#FF5A1F] via-[#FF6B35] to-[#FF5A1F] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-72 h-72 animate-pulse delay-200">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white">
              <path d="M0,0 L200,50 L150,200 L0,150 Z" fill="currentColor" />
            </svg>
          </div>
          <div className="absolute bottom-0 right-0 w-72 h-72 animate-pulse delay-800">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white">
              <path d="M50,0 L200,0 L200,200 L0,150 Z" fill="currentColor" />
            </svg>
          </div>
          <div className="absolute top-1/2 left-1/4 w-48 h-48 animate-pulse delay-1200">
            <svg viewBox="0 0 200 200" className="w-full h-full text-white/60">
              <path d="M20,20 L180,30 L170,180 L30,170 Z" fill="currentColor" />
            </svg>
          </div>
          {/* Floating particles */}
          <div className="absolute top-1/5 left-1/5 w-5 h-5 bg-white/30 rounded-full animate-bounce delay-400"></div>
          <div className="absolute top-4/5 right-1/5 w-4 h-4 bg-white/25 rounded-full animate-bounce delay-600"></div>
          <div className="absolute top-2/5 left-4/5 w-3 h-3 bg-white/35 rounded-full animate-bounce delay-200"></div>
          <div className="absolute top-3/5 right-2/5 w-3 h-3 bg-white/28 rounded-full animate-bounce delay-800"></div>
          <div className="absolute bottom-1/5 left-2/5 w-2 h-2 bg-white/40 rounded-full animate-bounce delay-1000"></div>
          <div className="absolute top-1/6 right-1/3 w-2 h-2 bg-white/32 rounded-full animate-bounce delay-1400"></div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
              {getField("why_choose_title", "Why Choose Rolleston Homes?")}
            </h2>
            <p className="text-lg text-white/95 max-w-3xl mx-auto">
              {getField(
                "why_choose_subtitle",
                "We don't just build houses, we create homes where memories are made and families flourish"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-white transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-[#1F2937] to-[#374151] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-4">
                Quality Craftsmanship
              </h3>
              <p className="text-[#334155] leading-relaxed">
                Every home is built with meticulous attention to detail using
                premium materials and time-tested construction methods.
              </p>
            </div>

            <div className="group bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-white transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-[#16A34A] to-[#22C55E] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-4">
                Personalized Service
              </h3>
              <p className="text-[#334155] leading-relaxed">
                From concept to completion, our dedicated team works closely
                with you to bring your vision to life.
              </p>
            </div>

            <div className="group bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-white transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-4">
                Award-Winning Designs
              </h3>
              <p className="text-[#334155] leading-relaxed">
                Our innovative designs have been recognized for excellence in
                both functionality and aesthetic appeal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Home Categories - Simple Cards Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("categories_title", "Explore Our Home Collections")}
            </h2>
            <p className="text-lg text-[#334155] max-w-3xl mx-auto">
              {getField(
                "categories_subtitle",
                "Discover our beautifully designed home categories, each crafted to meet different lifestyle needs and preferences."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Core Homes Card */}
            <Link
              to="/homes/core"
              className="group bg-gradient-to-br from-[#FAFAFA] to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={getField(
                    "core_homes_image",
                    "/assets/Core_Homes/Artist_Impressions/Plan01_Artist_Impression.jpg"
                  )}
                  alt="Core Homes - Plan01 Artist Impression"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <ArrowRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-[#1F2937] mb-4 group-hover:text-blue-600 transition-colors duration-300">
                  {getField("core_homes_title", "Core Homes")}
                </h3>
                <p className="text-[#334155] leading-relaxed mb-4">
                  {getField(
                    "core_homes_description",
                    "Essential living spaces designed for modern lifestyles. Our Core Homes collection offers practical, well-designed homes that provide excellent value."
                  )}
                </p>
                <div className="flex items-center text-blue-600 font-semibold">
                  <span>Explore Core Homes</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>

            {/* Family Homes Card */}
            <Link
              to="/homes/family"
              className="group bg-gradient-to-br from-[#FAFAFA] to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={getField(
                    "family_homes_image",
                    "/assets/Family_Homes/Artist_Impressions/Plan01_Artist_Impression.jpg"
                  )}
                  alt="Family Homes - Plan01 Artist Impression"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <ArrowRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-[#1F2937] mb-4 group-hover:text-green-600 transition-colors duration-300">
                  {getField("family_homes_title", "Family Homes")}
                </h3>
                <p className="text-[#334155] leading-relaxed mb-4">
                  {getField(
                    "family_homes_description",
                    "Spacious designs perfect for growing families. Our Family Homes feature multiple living areas, generous bedrooms, and thoughtful layouts."
                  )}
                </p>
                <div className="flex items-center text-green-600 font-semibold">
                  <span>Explore Family Homes</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>

            {/* Kiwiana Homes Card */}
            <Link
              to="/homes/kiwiana"
              className="group bg-gradient-to-br from-[#FAFAFA] to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={getField(
                    "kiwiana_homes_image",
                    "/assets/Kiwiana_Homes/Artist_Impressions/Plan01_Artist_Impression.jpg"
                  )}
                  alt="Kiwiana Homes - Plan01 Artist Impression"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <ArrowRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-[#1F2937] mb-4 group-hover:text-amber-600 transition-colors duration-300">
                  {getField("kiwiana_homes_title", "Kiwiana Homes")}
                </h3>
                <p className="text-[#334155] leading-relaxed mb-4">
                  {getField(
                    "kiwiana_homes_description",
                    "Celebrating New Zealand's unique architectural heritage. Our Kiwiana collection combines traditional Kiwi design elements with modern comfort."
                  )}
                </p>
                <div className="flex items-center text-amber-600 font-semibold">
                  <span>Explore Kiwiana Homes</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>
          </div>

          {/* View All Homes Button */}
          <div className="text-center">
            <Link
              to="/homes"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#FF5A1F] to-[#FF6B35] text-white px-10 py-5 rounded-2xl font-bold text-lg hover:from-[#e4501c] hover:to-[#e55a2e] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-105 group"
            >
              <span>View All Our Homes</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Enhanced Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1F2937] via-[#374151] to-[#1F2937] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#FF5A1F] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF5A1F] rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-8 animate-fade-in-up">
            {getField("final_cta_title", "Ready to Build Your Dream Home?")}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed animate-fade-in-up delay-200">
            {getField(
              "final_cta_subtitle",
              "Let's discuss your vision and create a home that perfectly suits your lifestyle and budget. Our experienced team is here to guide you through every step of the process."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in-up delay-400">
            <Link
              to={getField("final_cta_button1_link", "/contact")}
              className="bg-gradient-to-r from-[#FF5A1F] to-[#FF6B35] text-white px-10 py-5 rounded-2xl font-bold text-lg hover:from-[#e4501c] hover:to-[#e55a2e] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-105 group"
            >
              <span className="flex items-center space-x-2">
                <span>
                  {getField("final_cta_button1_text", "Start Your Journey")}
                </span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Link>
            <Link
              to={getField("final_cta_button2_link", "/homes")}
              className="border-2 border-white text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-[#1F2937] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-105"
            >
              {getField("final_cta_button2_text", "Browse Our Homes")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
